import { useState, useEffect } from 'react';
import { apiService } from '../../lib/api';
import type {
  SuccessStory,
  CreateSuccessStoryInput,
  CreateImpactMetricInput,
  CreateCustomerQuoteInput,
  CreateDifferentiatorInput,
  CreateImplementationPhaseInput,
  CreateRoiMetricInput,
  CreateProblemStatementInput,
} from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Save, ArrowLeft, Plus, Trash2, Loader2 } from 'lucide-react';

interface StoryEditorProps {
  story: SuccessStory | null;
  onSave: () => void;
  onCancel: () => void;
}

export function StoryEditor({ story, onSave, onCancel }: StoryEditorProps) {
  const [saving, setSaving] = useState(false);
  const [storyData, setStoryData] = useState<CreateSuccessStoryInput>({
    customerName: '',
    industry: '',
    companySize: '',
    headline: '',
    summary: '',
    situationText: '',
    problemText: '',
    solutionText: '',
    whyChosenText: '',
    resultsText: '',
    slug: '',
    status: 'draft',
  });

  const [metrics, setMetrics] = useState<CreateImpactMetricInput[]>([]);
  const [quotes, setQuotes] = useState<CreateCustomerQuoteInput[]>([]);
  const [differentiators, setDifferentiators] = useState<CreateDifferentiatorInput[]>([]);
  const [phases, setPhases] = useState<CreateImplementationPhaseInput[]>([]);
  const [roiMetrics, setRoiMetrics] = useState<CreateRoiMetricInput[]>([]);
  const [problems, setProblems] = useState<CreateProblemStatementInput[]>([]);

  useEffect(() => {
    if (story) {
      setStoryData({
        customerName: story.customerName,
        industry: story.industry,
        companySize: story.companySize,
        headline: story.headline,
        summary: story.summary,
        heroImageUrl: story.heroImageUrl || undefined,
        logoUrl: story.logoUrl || undefined,
        situationText: story.situationText,
        problemText: story.problemText,
        solutionText: story.solutionText,
        whyChosenText: story.whyChosenText,
        resultsText: story.resultsText,
        ctaText: story.ctaText,
        ctaUrl: story.ctaUrl,
        metaTitle: story.metaTitle || undefined,
        metaDescription: story.metaDescription || undefined,
        slug: story.slug,
        status: story.status,
      });
      loadRelatedData(story.id);
    }
  }, [story]);

  const loadRelatedData = async (storyId: string) => {
    try {
      const [metricsRes, quotesRes, diffsRes, phasesRes, roiRes, problemsRes] = await Promise.all([
        apiService.getMetricsByStoryId(storyId),
        apiService.getQuotesByStoryId(storyId),
        apiService.getDifferentiatorsByStoryId(storyId),
        apiService.getPhasesByStoryId(storyId),
        apiService.getRoiMetricsByStoryId(storyId),
        apiService.getProblemStatementsByStoryId(storyId),
      ]);

      if (metricsRes.success) setMetrics(metricsRes.data);
      if (quotesRes.success) setQuotes(quotesRes.data);
      if (diffsRes.success) setDifferentiators(diffsRes.data);
      if (phasesRes.success) setPhases(phasesRes.data);
      if (roiRes.success) setRoiMetrics(roiRes.data);
      if (problemsRes.success) setProblems(problemsRes.data);
    } catch (error) {
      console.error('Error loading related data:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      let storyId = story?.id;

      if (story) {
        const response = await apiService.updateStory(story.id, storyData);
        if (!response.success) throw new Error('Failed to update story');
      } else {
        const response = await apiService.createStory(storyData);
        if (!response.success) throw new Error('Failed to create story');
        storyId = response.data.id;
      }

      if (storyId) {
        await Promise.all([
          ...metrics.map((m) => apiService.createMetric(storyId!, m)),
          ...quotes.map((q) => apiService.createQuote(storyId!, q)),
          ...differentiators.map((d) => apiService.createDifferentiator(storyId!, d)),
          ...phases.map((p) => apiService.createPhase(storyId!, p)),
          ...roiMetrics.map((r) => apiService.createRoiMetric(storyId!, r)),
          ...problems.map((p) => apiService.createProblemStatement(storyId!, p)),
        ]);
      }

      toast.success(story ? 'Story updated successfully' : 'Story created successfully');
      onSave();
    } catch (error) {
      console.error('Error saving story:', error);
      toast.error('Failed to save story');
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = () => {
    const slug = storyData.customerName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    setStoryData({ ...storyData, slug });
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onCancel} className="border-border">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-foreground">
              {story ? 'Edit Story' : 'Create New Story'}
            </h1>
            <p className="text-muted-foreground">Fill in all sections to create a complete story</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground">
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Save Story
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList className="grid w-full grid-cols-7 lg:w-auto">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="hero">Hero Metrics</TabsTrigger>
          <TabsTrigger value="problem">Problem</TabsTrigger>
          <TabsTrigger value="solution">Solution</TabsTrigger>
          <TabsTrigger value="implementation">Implementation</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="seo">SEO & CTA</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Core details about the customer and their story</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customerName">Customer Name *</Label>
                  <Input
                    id="customerName"
                    value={storyData.customerName}
                    onChange={(e) => setStoryData({ ...storyData, customerName: e.target.value })}
                    placeholder="e.g., Fortune 500 Manufacturing Leader"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Input
                    id="industry"
                    value={storyData.industry}
                    onChange={(e) => setStoryData({ ...storyData, industry: e.target.value })}
                    placeholder="e.g., Manufacturing"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companySize">Company Size *</Label>
                  <Input
                    id="companySize"
                    value={storyData.companySize}
                    onChange={(e) => setStoryData({ ...storyData, companySize: e.target.value })}
                    placeholder="e.g., Fortune 500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <Select
                    value={storyData.status}
                    onValueChange={(value) =>
                      setStoryData({ ...storyData, status: value as 'draft' | 'published' | 'archived' })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="headline">Headline *</Label>
                <Input
                  id="headline"
                  value={storyData.headline}
                  onChange={(e) => setStoryData({ ...storyData, headline: e.target.value })}
                  placeholder="e.g., Global Manufacturing Leader Achieves 40% Faster Project Delivery"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary">Summary *</Label>
                <Textarea
                  id="summary"
                  value={storyData.summary}
                  onChange={(e) => setStoryData({ ...storyData, summary: e.target.value })}
                  placeholder="Brief overview of the transformation story"
                  rows={3}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="heroImageUrl">Hero Image URL</Label>
                  <Input
                    id="heroImageUrl"
                    value={storyData.heroImageUrl || ''}
                    onChange={(e) => setStoryData({ ...storyData, heroImageUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logoUrl">Logo URL</Label>
                  <Input
                    id="logoUrl"
                    value={storyData.logoUrl || ''}
                    onChange={(e) => setStoryData({ ...storyData, logoUrl: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hero" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Impact Metrics</CardTitle>
              <CardDescription>Key metrics displayed in the hero section (max 4 recommended)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {metrics.map((metric, index) => (
                <div key={index} className="flex gap-4 items-end">
                  <div className="flex-1 space-y-2">
                    <Label>Metric Value</Label>
                    <Input
                      value={metric.metricValue}
                      onChange={(e) => {
                        const newMetrics = [...metrics];
                        newMetrics[index].metricValue = e.target.value;
                        setMetrics(newMetrics);
                      }}
                      placeholder="e.g., 40%"
                    />
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label>Metric Label</Label>
                    <Input
                      value={metric.metricLabel}
                      onChange={(e) => {
                        const newMetrics = [...metrics];
                        newMetrics[index].metricLabel = e.target.value;
                        setMetrics(newMetrics);
                      }}
                      placeholder="e.g., Faster Delivery"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setMetrics(metrics.filter((_, i) => i !== index))}
                    className="border-destructive text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() =>
                  setMetrics([...metrics, { metricValue: '', metricLabel: '', displayOrder: metrics.length }])
                }
                className="w-full border-border"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Metric
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="problem" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Problem & Context</CardTitle>
              <CardDescription>Describe the customer's initial situation and challenges</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="situationText">The Situation *</Label>
                <Textarea
                  id="situationText"
                  value={storyData.situationText}
                  onChange={(e) => setStoryData({ ...storyData, situationText: e.target.value })}
                  placeholder="Describe the customer's initial context and environment"
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="problemText">The Problem *</Label>
                <Textarea
                  id="problemText"
                  value={storyData.problemText}
                  onChange={(e) => setStoryData({ ...storyData, problemText: e.target.value })}
                  placeholder="Describe the core challenges they faced"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Problem Statements</CardTitle>
              <CardDescription>Specific problem points (displayed as cards)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {problems.map((problem, index) => (
                <div key={index} className="space-y-4 p-4 border border-border rounded-lg">
                  <div className="flex justify-between items-start">
                    <Label>Problem {index + 1}</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setProblems(problems.filter((_, i) => i !== index))}
                      className="border-destructive text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    value={problem.title}
                    onChange={(e) => {
                      const newProblems = [...problems];
                      newProblems[index].title = e.target.value;
                      setProblems(newProblems);
                    }}
                    placeholder="Problem title"
                  />
                  <Textarea
                    value={problem.description}
                    onChange={(e) => {
                      const newProblems = [...problems];
                      newProblems[index].description = e.target.value;
                      setProblems(newProblems);
                    }}
                    placeholder="Problem description"
                    rows={3}
                  />
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() =>
                  setProblems([...problems, { title: '', description: '', displayOrder: problems.length }])
                }
                className="w-full border-border"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Problem Statement
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Customer Quotes (Problem Section)</CardTitle>
              <CardDescription>Quotes from customers about their challenges</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {quotes
                .filter((q) => q.section === 'problem')
                .map((quote, index) => (
                  <div key={index} className="space-y-4 p-4 border border-border rounded-lg">
                    <div className="flex justify-between items-start">
                      <Label>Quote {index + 1}</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setQuotes(quotes.filter((_, i) => i !== index))}
                        className="border-destructive text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <Textarea
                      value={quote.quoteText}
                      onChange={(e) => {
                        const newQuotes = [...quotes];
                        newQuotes[index].quoteText = e.target.value;
                        setQuotes(newQuotes);
                      }}
                      placeholder="Quote text"
                      rows={3}
                    />
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        value={quote.authorName}
                        onChange={(e) => {
                          const newQuotes = [...quotes];
                          newQuotes[index].authorName = e.target.value;
                          setQuotes(newQuotes);
                        }}
                        placeholder="Author name"
                      />
                      <Input
                        value={quote.authorTitle}
                        onChange={(e) => {
                          const newQuotes = [...quotes];
                          newQuotes[index].authorTitle = e.target.value;
                          setQuotes(newQuotes);
                        }}
                        placeholder="Author title"
                      />
                    </div>
                  </div>
                ))}
              <Button
                variant="outline"
                onClick={() =>
                  setQuotes([
                    ...quotes,
                    {
                      quoteText: '',
                      authorName: '',
                      authorTitle: '',
                      section: 'problem',
                      displayOrder: quotes.length,
                    },
                  ])
                }
                className="w-full border-border"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Quote
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="solution" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Solution Description</CardTitle>
              <CardDescription>Explain the solution and why they chose Planisware</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="solutionText">Solution Overview *</Label>
                <Textarea
                  id="solutionText"
                  value={storyData.solutionText}
                  onChange={(e) => setStoryData({ ...storyData, solutionText: e.target.value })}
                  placeholder="Describe the solution implemented"
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="whyChosenText">Why They Chose Planisware *</Label>
                <Textarea
                  id="whyChosenText"
                  value={storyData.whyChosenText}
                  onChange={(e) => setStoryData({ ...storyData, whyChosenText: e.target.value })}
                  placeholder="Explain the decision criteria and selection process"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Differentiators</CardTitle>
              <CardDescription>Unique value propositions (max 4 recommended)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {differentiators.map((diff, index) => (
                <div key={index} className="space-y-4 p-4 border border-border rounded-lg">
                  <div className="flex justify-between items-start">
                    <Label>Differentiator {index + 1}</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDifferentiators(differentiators.filter((_, i) => i !== index))}
                      className="border-destructive text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    value={diff.title}
                    onChange={(e) => {
                      const newDiffs = [...differentiators];
                      newDiffs[index].title = e.target.value;
                      setDifferentiators(newDiffs);
                    }}
                    placeholder="Differentiator title"
                  />
                  <Textarea
                    value={diff.description}
                    onChange={(e) => {
                      const newDiffs = [...differentiators];
                      newDiffs[index].description = e.target.value;
                      setDifferentiators(newDiffs);
                    }}
                    placeholder="Differentiator description"
                    rows={3}
                  />
                  <Select
                    value={diff.icon || 'chart'}
                    onValueChange={(value) => {
                      const newDiffs = [...differentiators];
                      newDiffs[index].icon = value;
                      setDifferentiators(newDiffs);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select icon" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chart">Chart</SelectItem>
                      <SelectItem value="zap">Zap</SelectItem>
                      <SelectItem value="settings">Settings</SelectItem>
                      <SelectItem value="users">Users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() =>
                  setDifferentiators([
                    ...differentiators,
                    { title: '', description: '', icon: 'chart', displayOrder: differentiators.length },
                  ])
                }
                className="w-full border-border"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Differentiator
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="implementation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Implementation Phases</CardTitle>
              <CardDescription>Timeline of the rollout process</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {phases.map((phase, index) => (
                <div key={index} className="space-y-4 p-4 border border-border rounded-lg">
                  <div className="flex justify-between items-start">
                    <Label>Phase {index + 1}</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPhases(phases.filter((_, i) => i !== index))}
                      className="border-destructive text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      value={phase.phaseName}
                      onChange={(e) => {
                        const newPhases = [...phases];
                        newPhases[index].phaseName = e.target.value;
                        setPhases(newPhases);
                      }}
                      placeholder="Phase name (e.g., Phase 1: Foundation)"
                    />
                    <Input
                      value={phase.duration}
                      onChange={(e) => {
                        const newPhases = [...phases];
                        newPhases[index].duration = e.target.value;
                        setPhases(newPhases);
                      }}
                      placeholder="Duration (e.g., Months 1-4)"
                    />
                  </div>
                  <Textarea
                    value={phase.activities}
                    onChange={(e) => {
                      const newPhases = [...phases];
                      newPhases[index].activities = e.target.value;
                      setPhases(newPhases);
                    }}
                    placeholder="Activities (one per line)"
                    rows={4}
                  />
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() =>
                  setPhases([
                    ...phases,
                    { phaseName: '', duration: '', activities: '', displayOrder: phases.length },
                  ])
                }
                className="w-full border-border"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Phase
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="results" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Results Overview</CardTitle>
              <CardDescription>Summary of the transformation results</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="resultsText">Results Summary *</Label>
                <Textarea
                  id="resultsText"
                  value={storyData.resultsText}
                  onChange={(e) => setStoryData({ ...storyData, resultsText: e.target.value })}
                  placeholder="Describe the overall business impact and transformation"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>ROI Metrics (Before/After)</CardTitle>
              <CardDescription>Quantifiable improvements with before and after values</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {roiMetrics.map((metric, index) => (
                <div key={index} className="space-y-4 p-4 border border-border rounded-lg">
                  <div className="flex justify-between items-start">
                    <Label>Metric {index + 1}</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRoiMetrics(roiMetrics.filter((_, i) => i !== index))}
                      className="border-destructive text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Input
                    value={metric.metricName}
                    onChange={(e) => {
                      const newMetrics = [...roiMetrics];
                      newMetrics[index].metricName = e.target.value;
                      setRoiMetrics(newMetrics);
                    }}
                    placeholder="Metric name (e.g., Average Project Delivery Time)"
                  />
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      value={metric.beforeValue}
                      onChange={(e) => {
                        const newMetrics = [...roiMetrics];
                        newMetrics[index].beforeValue = e.target.value;
                        setRoiMetrics(newMetrics);
                      }}
                      placeholder="Before value (e.g., 18 months)"
                    />
                    <Input
                      value={metric.afterValue}
                      onChange={(e) => {
                        const newMetrics = [...roiMetrics];
                        newMetrics[index].afterValue = e.target.value;
                        setRoiMetrics(newMetrics);
                      }}
                      placeholder="After value (e.g., 10.8 months)"
                    />
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() =>
                  setRoiMetrics([
                    ...roiMetrics,
                    { metricName: '', beforeValue: '', afterValue: '', displayOrder: roiMetrics.length },
                  ])
                }
                className="w-full border-border"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add ROI Metric
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="seo" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>SEO & Publishing</CardTitle>
              <CardDescription>Search engine optimization and URL settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="slug">URL Slug *</Label>
                <div className="flex gap-2">
                  <Input
                    id="slug"
                    value={storyData.slug}
                    onChange={(e) => setStoryData({ ...storyData, slug: e.target.value })}
                    placeholder="customer-name-success-story"
                  />
                  <Button variant="outline" onClick={generateSlug} className="border-border">
                    Generate
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="metaTitle">Meta Title</Label>
                <Input
                  id="metaTitle"
                  value={storyData.metaTitle || ''}
                  onChange={(e) => setStoryData({ ...storyData, metaTitle: e.target.value })}
                  placeholder="SEO title (60 characters max)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="metaDescription">Meta Description</Label>
                <Textarea
                  id="metaDescription"
                  value={storyData.metaDescription || ''}
                  onChange={(e) => setStoryData({ ...storyData, metaDescription: e.target.value })}
                  placeholder="SEO description (160 characters max)"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Call-to-Action</CardTitle>
              <CardDescription>CTA button configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ctaText">CTA Button Text</Label>
                  <Input
                    id="ctaText"
                    value={storyData.ctaText}
                    onChange={(e) => setStoryData({ ...storyData, ctaText: e.target.value })}
                    placeholder="Request Demo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ctaUrl">CTA URL</Label>
                  <Input
                    id="ctaUrl"
                    value={storyData.ctaUrl}
                    onChange={(e) => setStoryData({ ...storyData, ctaUrl: e.target.value })}
                    placeholder="/demo"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
